import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { FORMATIONS, type Cloud, type FormationKey } from "../formations";
import { CURATED_FONTS, queryInstalledFonts, type RenderMode } from "../sources";
import {
  buildCloud, cymaticsLadder, DEFAULT_CYMATICS, DEFAULT_LISSAJOUS,
  type CymaticsParams, type LissajousParams, type Source, type VisStyle,
} from "../source";
import { DEFAULT_GRADIENT, gradientColors, type GradientAxis, type GradientStop } from "../gradient";
import { audioBus } from "../audioBus";

export type ColorMode = "source" | "solid" | "gradient";
export type { Source };

type Opts = {
  onError: (msg: string) => void;
};

/**
 * Owns "what shape are the drones making": the active source (formation / text
 * / SVG / image), drone count, size, render mode, fonts, and the derived point
 * cloud. Rebuilds the cloud whenever any of those change.
 */
export function useSwarmSource({ onError }: Opts) {
  const [source, setSource] = useState<Source>({ kind: "formation", key: "sphere" });
  const [count, setCount] = useState(6000);
  const [baseCloud, setBaseCloud] = useState<Cloud>(() => FORMATIONS.sphere(6000));
  const [sourceScale, setSourceScale] = useState(1);
  const [textInput, setTextInput] = useState("FREE.VET");
  const [font, setFont] = useState("Arial");
  const [fontList, setFontList] = useState<string[]>(CURATED_FONTS);
  const [renderMode, setRenderMode] = useState<RenderMode>("outline");
  const [colorMode, setColorMode] = useState<ColorMode>("source");
  const [solidColor, setSolidColor] = useState("#ffffff");
  const [gradient, setGradient] = useState<GradientStop[]>(DEFAULT_GRADIENT);
  const [gradientAxis, setGradientAxis] = useState<GradientAxis>("y");
  const [liss, setLiss] = useState<LissajousParams>(DEFAULT_LISSAJOUS);
  const [flowOn, setFlowOn] = useState(false);
  const [flowSpeed, setFlowSpeed] = useState(5); // laps / minute
  const [cym, setCym] = useState<CymaticsParams>(DEFAULT_CYMATICS);
  const [cymAmp, setCymAmp] = useState(0.7);
  const [sweepOn, setSweepOn] = useState(false);
  const [audioSweepOn, setAudioSweepOn] = useState(false);
  const [energySweepOn, setEnergySweepOn] = useState(false);
  const [sweepRate, setSweepRate] = useState(0.8); // modes / second
  const [sweepLo, setSweepLo] = useState(1);
  const [sweepHi, setSweepHi] = useState(cymaticsLadder(-1).length);

  // Size (sourceScale) is applied as a uniform transform on the rendered swarm
  // (see StudioScene's ScaledSwarm), NOT baked into the cloud. Baking it in made
  // every Size step a new target cloud → a re-target/re-assign that could shove
  // drones across the shape (worst in neighbor mode, where scaling down crowded
  // them below the safe zone and separation flung them to the far side). Keeping
  // the cloud canonical means Size just scales everything in place, uniformly.
  //
  // Colour, on the other hand, IS part of the cloud — but "solid" reuses the
  // SAME positions array by reference, so switching colour is a pure colour
  // crossfade (positions morph X→X = no movement), not a reshuffle.
  const cloud = useMemo<Cloud>(() => {
    if (colorMode === "source") return baseCloud;
    if (colorMode === "gradient") {
      return { positions: baseCloud.positions, colors: gradientColors(baseCloud.positions, gradient, gradientAxis) };
    }
    const c = new THREE.Color(solidColor);
    const colors = new Float32Array(baseCloud.colors.length);
    for (let i = 0; i < colors.length; i += 3) {
      colors[i] = c.r;
      colors[i + 1] = c.g;
      colors[i + 2] = c.b;
    }
    return { positions: baseCloud.positions, colors };
  }, [baseCloud, colorMode, solidColor, gradient, gradientAxis]);

  // rebuild the base cloud whenever the source, count or render mode changes
  useEffect(() => {
    let cancelled = false;
    onError("");
    buildCloud(source, count, renderMode)
      .then((cl) => { if (!cancelled) setBaseCloud(cl); })
      .catch((e) => { if (!cancelled) onError(e instanceof Error ? e.message : String(e)); });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, count, renderMode]);

  function pickFormation(key: FormationKey) {
    setSource({ kind: "formation", key });
  }
  function spell() {
    setSource({ kind: "text", text: textInput.trim() || " ", font });
  }
  function onFontChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const f = e.target.value;
    setFont(f);
    setSource((s) => (s.kind === "text" ? { ...s, font: f } : s));
  }
  async function loadSystemFonts() {
    try {
      const fams = await queryInstalledFonts();
      if (fams.length) setFontList(fams);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  }
  function onSvgFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) f.text().then((svg) => setSource({ kind: "svg", name: f.name, svg }));
    e.target.value = "";
  }
  function onImgFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) setSource({ kind: "image", name: f.name, url: URL.createObjectURL(f) });
    e.target.value = "";
  }
  /** Show the current lissajous curve (native, parametric — no image). */
  function applyLissajous() {
    setSource({ kind: "lissajous", ...liss });
  }
  /** Tune one lissajous dial; if the curve is on screen it reshapes live. */
  const setLissParam = (k: keyof LissajousParams) => (v: number) => {
    const nextP = { ...liss, [k]: v };
    setLiss(nextP);
    setSource((src) => (src.kind === "lissajous" ? { kind: "lissajous", ...nextP } : src));
  };
  /** Flow: the swarm streams around the curve (neighbor physics). */
  function toggleFlow() {
    if (!flowOn && source.kind !== "lissajous") applyLissajous();
    setFlowOn((f) => !f);
  }

  // ---- the audio visualizer (a live source — positions come from the analyser) ----
  function applyVisualizer(style: VisStyle) {
    setSource({ kind: "visualizer", style });
  }

  // ---- cymatics (Chladni plate) ----
  const ladder = cymaticsLadder(cym.mix);

  /** Show the plate with the current mode. */
  function applyCymatics() {
    setSource({ kind: "cymatics", ...cym });
  }
  /** Tune the plate; manual edits take the wheel back from a running sweep. */
  const setCymParam = (patch: Partial<CymaticsParams>, stopSweep = true) => {
    if (stopSweep) { setSweepOn(false); setAudioSweepOn(false); setEnergySweepOn(false); }
    const nextP = { ...cym, ...patch };
    setCym(nextP);
    setSource((src) => (src.kind === "cymatics" ? { kind: "cymatics", ...nextP } : src));
  };

  // one step up the plate's mode ladder (eigenfrequency order), cycling
  // within [lo, hi] — shared by the timed sweep and the beat sweep
  const sweepIdx = useRef(0);
  const advanceLadder = () => {
    const lo = Math.max(1, Math.min(sweepLo, ladder.length)) - 1;
    const hi = Math.max(1, Math.min(sweepHi, ladder.length)) - 1;
    const a = Math.min(lo, hi), b = Math.max(lo, hi);
    sweepIdx.current = sweepIdx.current < a || sweepIdx.current >= b ? a : sweepIdx.current + 1;
    const mode = ladder[sweepIdx.current];
    const nextP = { ...cym, m: mode.m, n: mode.n };
    setCym(nextP);
    setSource({ kind: "cymatics", ...nextP });
  };

  // the frequency sweep: climb the ladder on a timer —
  // the tone-generator-on-a-real-plate experience
  useEffect(() => {
    if (!sweepOn) return;
    const id = window.setInterval(advanceLadder, Math.max(120, 1000 / sweepRate));
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sweepOn, sweepRate, sweepLo, sweepHi, cym.mix, cym.form, cym.span]);

  // the BEAT sweep: the track picks the figure — advance on each kick's
  // rising edge, with a refractory so fast tracks melt rather than strobe
  // (the figures ease between modes in the fields, so each step flows)
  const beatSweep = useRef({ prevBeat: 0, lastAdv: 0 });
  useEffect(() => {
    if (!audioSweepOn) return;
    const id = window.setInterval(() => {
      const st = beatSweep.current;
      const b = audioBus.values.beat;
      const edge = b > 0.9 && st.prevBeat < 0.5;
      st.prevBeat = b;
      const now = performance.now();
      if (!edge || now - st.lastAdv < 900) return;
      st.lastAdv = now;
      advanceLadder();
    }, 40);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSweepOn, sweepLo, sweepHi, cym.mix, cym.form, cym.span]);

  // the ENERGY sweep: sustained loudness picks the pace — accumulate
  // level·dt and advance each time the integral crosses a threshold. A loud
  // passage sweeps steadily, a quiet one holds the figure. (Beat = punchy
  // kicks; Energy = the swell of the whole track. Both are fascinating.)
  const energySweep = useRef({ acc: 0, lastT: 0, lastAdv: 0 });
  useEffect(() => {
    if (!energySweepOn) return;
    energySweep.current.lastT = performance.now();
    energySweep.current.acc = 0;
    const id = window.setInterval(() => {
      const st = energySweep.current;
      const now = performance.now();
      const dt = Math.min((now - st.lastT) / 1000, 0.25);
      st.lastT = now;
      st.acc += audioBus.values.level * dt;
      if (st.acc < 1.1 || now - st.lastAdv < 700) return; // ~2 s at level 0.5
      st.acc = 0;
      st.lastAdv = now;
      advanceLadder();
    }, 250); // an integrator needs no 40 ms polling — and coarse ticks survive tab throttling far better
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energySweepOn, sweepLo, sweepHi, cym.mix, cym.form, cym.span]);

  function toggleSweep() {
    if (!sweepOn) { applyCymatics(); setAudioSweepOn(false); setEnergySweepOn(false); }
    setSweepOn((s) => !s);
  }
  function toggleAudioSweep() {
    if (!audioSweepOn) { applyCymatics(); setSweepOn(false); setEnergySweepOn(false); }
    setAudioSweepOn((s) => !s);
  }
  function toggleEnergySweep() {
    if (!energySweepOn) { applyCymatics(); setSweepOn(false); setAudioSweepOn(false); }
    setEnergySweepOn((s) => !s);
  }

  /** Live standing-wave drive for the shader (null when not on the plate). */
  const cymDrive = source.kind === "cymatics" ? { m: source.m, n: source.n, mix: source.mix, amp: cymAmp, speed: 2.4 } : null;
  /** Curve-flow drive for the neighbor field (null unless flowing a lissajous). */
  const flowDrive = source.kind === "lissajous" && flowOn ? { speed: flowSpeed } : null;
  /** Active visualizer style (null when not in visualizer mode). */
  const visStyle = source.kind === "visualizer" ? source.style : null;

  function pickSolid(hex: string) {
    setSolidColor(hex);
    setColorMode("solid");
  }

  return {
    cloud, source, count, setCount, sourceScale, setSourceScale,
    textInput, setTextInput, font, fontList, renderMode, setRenderMode,
    colorMode, setColorMode, solidColor, setSolidColor, pickSolid,
    gradient, setGradient, gradientAxis, setGradientAxis,
    pickFormation, spell, onFontChange, loadSystemFonts, onSvgFile, onImgFile,
    liss, applyLissajous, setLissParam,
    flowOn, toggleFlow, flowSpeed, setFlowSpeed, flowDrive,
    applyVisualizer, visStyle,
    cym, cymAmp, setCymAmp, applyCymatics, setCymParam, cymDrive, ladderLength: ladder.length,
    sweepOn, toggleSweep, sweepRate, setSweepRate, sweepLo, setSweepLo, sweepHi, setSweepHi,
    audioSweepOn, toggleAudioSweep, energySweepOn, toggleEnergySweep,
  };
}
